/**
 * apps/web :: pass :: screens/PassHome
 *
 * The REAL unlocked pack-home dashboard (ROADS-569 W1.B), matching the
 * Pencil "pack home" reference. Replaces the W0.2 scaffold
 * (`apps/web/src/pass/PassHome.tsx`) — that placeholder stays mounted as
 * the `/pass` index until Wave 2 re-points the index route at this screen
 * via `passHomeRoutes` (see `passHome.routes.tsx`).
 *
 * Surfaces:
 *   - A "Start driving mode" hero CTA row (the primary action).
 *   - A quick grid of the pass sections — rules / plan / learn /
 *     emergency / waitlist — as relative react-router <Link>s.
 *   - The active country read from `useActivePassCountries()`, fed into
 *     PassLayout so its header resolves the pack name + freshness badge.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { Link } from "react-router-dom";

import PassLayout from "../PassLayout";
import { getPack, useActivePassCountries, type PackId } from "@/lib/pass-content";

interface QuickLink {
  to: string;
  label: string;
  hint: string;
}

/** The pack sections Wave-1/2 screens register under `/pass/*`. */
const QUICK_LINKS: QuickLink[] = [
  { to: "rules", label: "Rules", hint: "Local road rules at a glance" },
  { to: "plan", label: "Plan your trip", hint: "Pre / on / post checklists" },
  { to: "learn", label: "Learn", hint: "Quick lessons before you drive" },
  { to: "emergency", label: "Emergency card", hint: "Numbers + phrases, offline" },
  { to: "waitlist", label: "Waitlist", hint: "More countries coming" },
];

/** Resolve a pack name for display from a (validated) country code. */
function packName(code: string): string {
  try {
    return getPack(code as PackId).jurisdiction.name;
  } catch {
    return code;
  }
}

export default function PassHome(): JSX.Element {
  const countries = useActivePassCountries();
  const primary = countries[0];

  return (
    <PassLayout country={primary} title="Your pass">
      {primary ? (
        <p
          className="text-sm font-semibold text-indigo-700"
          data-testid="pass-home-active-country"
        >
          {packName(primary)}
        </p>
      ) : (
        <p
          className="text-sm text-neutral-500"
          data-testid="pass-home-no-countries"
        >
          No active travel packs yet.
        </p>
      )}

      {/* Primary CTA row — "Start driving mode". */}
      <Link
        to="rules"
        data-testid="pass-home-drive-cta"
        className="mt-md flex items-center justify-between rounded-xl bg-indigo-600 px-md py-md text-white no-underline hover:bg-indigo-700"
      >
        <span className="text-base font-semibold">Start driving mode</span>
        <span aria-hidden="true" className="text-lg">
          →
        </span>
      </Link>

      {/* Quick grid linking to the pass sections. */}
      <nav className="mt-lg" aria-label="Pass sections">
        <ul
          className="grid grid-cols-2 gap-2"
          data-testid="pass-home-quick-grid"
        >
          {QUICK_LINKS.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                data-testid={`pass-home-link-${link.to}`}
                className="flex h-full flex-col gap-1 rounded-md border border-neutral-200 px-md py-sm no-underline hover:border-indigo-300 hover:bg-indigo-50"
              >
                <span className="text-sm font-semibold text-brand-primary">
                  {link.label}
                </span>
                <span className="text-xs text-neutral-500">{link.hint}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </PassLayout>
  );
}
